import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Plus, Save, Tv, Layers, Lock, ChevronDown, ChevronUp,
  Brain, Loader2, Send, FileDown, X, Trash2, History,
} from 'lucide-react';
import VersionHistoryModal from '../components/VersionHistoryModal';
import { Button } from '../components/ui/button';
import { AutoExpandTextarea } from '../components/AutoExpandTextarea';
import { toast } from 'sonner';
import { authFetch } from '../lib/authFetch';
import { isStudioTier } from '../lib/pricing';
import { useTranslation } from 'react-i18next';
import { useProject } from '../context/ProjectContext';
import { CharactersTab, CharacterWebTab, ScreenplayTab } from './ProjectWorkspace';
import WorldBuilding from './WorldBuilding';
import NotesTab from './NotesTab';
import SplitViewLayout, { SplitViewTrigger } from '../components/SplitViewLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Modules that exist inside Series Builder and can be paired in split view.
// The native Series modules (bible / seasons / episodes) and the borrowed
// workspace modules (notes / characters / web / world) all render through
// renderSeriesModule(), so any pair can sit side by side.
// (Plot Forge / Scene Builder / Story Bible are workspace-only — excluded.)
const SERIES_SPLIT_TABS = ['bible', 'seasons', 'episodes', 'screenplay', 'notes', 'characters', 'web', 'world'];

// Series-local labels for the split picker + pane headers. NB: inside Series
// Builder the tab id `bible` means *Series* Bible (not the workspace Story
// Bible), so we can't reuse the workspace SPLIT_TAB_LABELS map here.
const SERIES_TAB_LABELS = {
  bible:      'Series Bible',
  seasons:    'Season Arc',
  episodes:   'Episodes',
  screenplay: 'Script',
  characters: 'Character Lab',
  web:        'Character Web',
  world:      'World Building',
  notes:      'Notes & References',
};

// ─── Beat definitions ──────────────────────────────────────────────────────

const PILOT_BEATS = [
  { key: 'pt_hook',           label: 'The Hook',                act: 1, placeholder: 'Cold open. What image, scene, or moment immediately establishes the tone and world of this show?' },
  { key: 'pt_world',          label: 'The World',               act: 1, placeholder: "Establish the show's arena. What is the setting, society, or system your story lives in?" },
  { key: 'pt_protagonist',    label: 'The Protagonist',         act: 1, placeholder: 'Who do we follow and why should we care? What do they want and what do they need?' },
  { key: 'pt_status_quo',     label: 'The Status Quo',          act: 1, placeholder: 'What is normal life in this world before everything changes?' },
  { key: 'pt_disruption',     label: 'The Inciting Disruption', act: 2, placeholder: 'What event breaks the status quo and cannot be ignored?' },
  { key: 'pt_problem',        label: 'The Problem',             act: 2, placeholder: 'State the central conflict that will drive the entire series, not just this episode.' },
  { key: 'pt_allies',         label: 'The Allies',              act: 2, placeholder: 'Who joins the protagonist? What does each relationship reveal about the world or the protagonist?' },
  { key: 'pt_opposition',     label: 'The Opposition',          act: 2, placeholder: 'What force opposes the protagonist? (person, institution, environment, internal, or unknown)' },
  { key: 'pt_attempt',        label: 'The First Attempt',       act: 2, placeholder: 'The protagonist tries to solve or confront the problem. How do they fail or partially succeed?' },
  { key: 'pt_revelation',     label: 'The Revelation',          act: 3, placeholder: 'Something is revealed that reframes everything — about the world, a character, or the central conflict.' },
  { key: 'pt_commitment',     label: 'The Commitment',          act: 3, placeholder: 'The protagonist makes a choice that locks them into the series conflict. There is no going back.' },
  { key: 'pt_series_promise', label: 'The Series Promise',      act: 3, placeholder: 'The final scene or image. What does this show promise to be about week after week?' },
];

const EPISODE_BEATS = [
  { key: 'ep_cold_open',   label: 'Cold Open',           act: 1, placeholder: 'What is the hook before the titles? What question does this episode ask?' },
  { key: 'ep_status_quo',  label: 'Status Quo',          act: 1, placeholder: "Where are we in the season arc? What is the characters' current state?" },
  { key: 'ep_trigger',     label: 'The Trigger',         act: 1, placeholder: 'What disrupts this specific episode?' },
  { key: 'ep_a_launch',    label: 'A-Story Launch',      act: 2, placeholder: 'What is the main plot of this episode and who drives it?' },
  { key: 'ep_b_launch',    label: 'B-Story Launch',      act: 2, placeholder: 'What is the secondary storyline and how does it contrast or mirror the A-story?' },
  { key: 'ep_collision',   label: 'Midpoint Collision',  act: 2, placeholder: 'How do the A and B stories intersect or complicate each other?' },
  { key: 'ep_escalation',  label: 'Escalation',          act: 2, placeholder: 'Stakes raise on both storylines. What makes it harder for each character?' },
  { key: 'ep_break',       label: 'The Break',           act: 2, placeholder: 'A character hits their emotional or physical limit. What breaks?' },
  { key: 'ep_convergence', label: 'Convergence',         act: 3, placeholder: 'How do the storylines collide toward resolution?' },
  { key: 'ep_tag',         label: 'The Tag',             act: 3, placeholder: 'Final beat. Does this resolve the episode, set up the next one, or deepen the season arc?' },
];

const ACT_LABELS = { 1: 'Act I', 2: 'Act II', 3: 'Act III' };

function getBeatsForEpisode(episodeNumber) {
  return episodeNumber === 1 ? PILOT_BEATS : EPISODE_BEATS;
}

const EMPTY_SEASON = (num) => ({
  season_number: num,
  logline: '',
  theme: '',
  character_arcs: '',
  mid_season_turn: '',
  finale_direction: '',
  episodes: [],
});

const EMPTY_EPISODE = (num) => ({
  episode_number: num,
  title: '',
  logline: '',
  beats: {},
});

// ─── Component ────────────────────────────────────────────────────────────

const SERIES_MENTOR_MODES = [
  { key: 'forge_guide', label: 'Series Coach',       desc: 'Socratic coaching on your series structure',       icon: '🔥' },
  { key: 'stress_test', label: 'Series Stress Test', desc: 'Ruthless challenge on premise, arcs, and episodes', icon: '⚡' },
];

export default function SeriesBuilder() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { i18n } = useTranslation();
  const { setActiveProject } = useProject();

  const [project,       setProject]       = useState(null);
  const [user,          setUser]          = useState(null);
  const [characters,    setCharacters]    = useState([]);
  const [relationships, setRelationships] = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [saving,        setSaving]        = useState(false);
  const [exportOpen,    setExportOpen]    = useState(false);
  const [historyOpen,   setHistoryOpen]   = useState(false);
  const [splitView,     setSplitView]     = useState(null); // null | { left, right }

  // Active tab lives in the URL (?tab=) exactly like ProjectWorkspace, so
  // the browser back/forward buttons move between tabs, deep links work,
  // and returning from character creation lands on the right tab.
  const SERIES_TABS = ['bible', 'seasons', 'episodes', 'screenplay', 'characters', 'web', 'world', 'notes', 'mentor'];
  const tabParam = searchParams.get('tab');
  const activeTab = SERIES_TABS.includes(tabParam) ? tabParam : 'bible';
  const setActiveTab = (id) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('tab', id);
      return p;
    });
  };
  const [activeSeason,  setActiveSeason]  = useState(0);
  const [activeEpisode, setActiveEpisode] = useState(null); // { seasonIdx, episodeIdx }
  const [expandedActs,  setExpandedActs]  = useState({ 1: true, 2: true, 3: true });
  // Screenplay session draft — survives tab switches within this Series
  // Builder session; ScreenplayTab serializes into it on unmount (same
  // pattern as ProjectWorkspace's screenplayDocRef).
  const screenplayDocRef = useRef(null);

  const [seriesData, setSeriesData] = useState({
    series_bible: { logline: '', premise: '', tone: '', platform: '', episode_count: '', season_count: '' },
    seasons: [],
  });

  useEffect(() => { fetchData(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [pRes, uRes, allProjectsRes] = await Promise.all([
        authFetch(`${API}/projects/${projectId}`, { credentials: 'include' }),
        authFetch(`${API}/auth/me`, { credentials: 'include' }),
        authFetch(`${API}/projects`, { credentials: 'include' }),
      ]);
      const pData = await pRes.json();
      const uData = await uRes.json();
      const allProjects = await allProjectsRes.json();

      setProject(pData);
      // Mirror ProjectWorkspace: seed global context so child pages
      // (CreateProject, WorldBuilding, SceneBuilder) know the active
      // workspace — without this, a character made here had no title.
      setActiveProject({
        plotProjectId: pData.project_id,
        projectTitle: pData.title,
        projectFormat: pData.format,
      });
      setUser(uData);
      setSeriesData({
        series_bible: pData.series_bible || { logline: '', premise: '', tone: '', platform: '', episode_count: '', season_count: '' },
        seasons: pData.seasons || [],
      });

      const chars = Array.isArray(allProjects)
        ? allProjects.filter(p => p.project_type === 'character' && p.workspace_id === projectId)
        : [];
      setCharacters(chars);

      if (['indie', 'studio'].includes(uData.subscription_tier)) {
        const relsRes = await authFetch(`${API}/relationships`, { credentials: 'include' });
        const relsData = await relsRes.json();
        setRelationships(Array.isArray(relsData) ? relsData : []);
      }
    } catch {
      toast.error('Failed to load series');
    } finally {
      setLoading(false);
    }
  };

  const isIndiePlus = ['indie', 'studio'].includes(user?.subscription_tier);

  // ─── Split view (shared with ProjectWorkspace) ──────────────────────────
  useEffect(() => {
    // Only restore pairings this host can render — a preference written by
    // ProjectWorkspace can reference workspace-only tabs (mirrors its guard).
    const pref = project?.split_view_preference;
    if (pref?.right && SERIES_SPLIT_TABS.includes(pref.right)) setSplitView(pref);
  }, [project?.project_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateSplitView = async (next) => {
    setSplitView(next);
    try {
      await authFetch(`${API}/projects/${projectId}/split-view`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(next || { left: null, right: null }),
      });
    } catch (e) {
      console.error('split-view persist failed', e);
    }
  };

  // Render a split-capable module with its SplitViewTrigger wired in via
  // splitProps (the trigger lives inside each module's own header row).
  const renderSeriesModule = (tabId, splitProps) => {
    switch (tabId) {
      // ── Native Series modules ──────────────────────────────────────────
      case 'bible':
        return (
          <div className="space-y-8">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h2 className="font-mono text-xl font-semibold mb-1">Series Bible</h2>
                <p className="text-muted-foreground text-sm font-mono">The foundational document — what the series is, who it's for, and what it promises.</p>
              </div>
              {splitProps && <SplitViewTrigger {...splitProps} />}
            </div>
            {[
              { field: 'logline',       label: 'Series Logline',      multiline: true,  placeholder: 'One to two sentences. Who is your protagonist, what do they want, what stands in their way, and what is at stake?' },
              { field: 'premise',       label: 'Premise',             multiline: true,  placeholder: 'The core dramatic situation. What is the world, who lives in it, and what central tension generates stories season after season?' },
              { field: 'tone',          label: 'Tone',                multiline: false, placeholder: 'e.g. "Dark thriller with dry humor" or "Coming-of-age drama, grounded, no supernatural elements"' },
              { field: 'platform',      label: 'Target Platform',     multiline: false, placeholder: 'e.g. Netflix, NTV Kenya, SHOWMAX Africa, film festival circuit' },
              { field: 'episode_count', label: 'Episodes Per Season', multiline: false, placeholder: 'e.g. 8, 10, 13' },
              { field: 'season_count',  label: 'Planned Seasons',     multiline: false, placeholder: 'e.g. 3 — or "open-ended"' },
            ].map(({ field, label, placeholder, multiline }) => (
              <div key={field}>
                <label className="block font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2">{label}</label>
                {/* All series-bible fields auto-expand; the multiline flag now
                    only controls the starting min-height. */}
                <AutoExpandTextarea
                  placeholder={placeholder}
                  value={seriesData.series_bible[field] || ''}
                  onChange={e => updateBible(field, e.target.value)}
                  className={`bg-background border-input rounded-none font-mono text-sm focus:ring-2 focus:ring-primary placeholder:text-muted-foreground/50 ${
                    multiline ? 'min-h-[90px]' : 'min-h-[60px]'
                  }`}
                />
              </div>
            ))}
          </div>
        );
      case 'seasons':
        return (
          <div>
            <div className="flex items-center justify-between mb-6 gap-3">
              <div>
                <h2 className="font-mono text-xl font-semibold mb-1">Season Arc</h2>
                <p className="text-muted-foreground text-sm font-mono">
                  {isIndiePlus ? 'Up to 3 seasons.' : 'Spark: 1 season. Upgrade to Indie for more.'}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {seriesData.seasons.length < 3 && (
                  <Button
                    onClick={addSeason}
                    variant="outline"
                    className="rounded-none font-mono uppercase tracking-widest text-xs"
                  >
                    <Plus className="w-3.5 h-3.5 mr-2" />
                    Add Season
                  </Button>
                )}
                {splitProps && <SplitViewTrigger {...splitProps} />}
              </div>
            </div>

            {seriesData.seasons.length === 0 && (
              <div className="border border-border/40 p-12 text-center">
                <Layers className="w-10 h-10 text-muted-foreground mx-auto mb-4" />
                <p className="font-mono text-sm text-muted-foreground mb-4">No seasons yet. Add Season 1 to start building the arc.</p>
                <Button onClick={addSeason} className="bg-primary text-primary-foreground rounded-none font-mono uppercase tracking-widest text-xs">
                  <Plus className="w-3.5 h-3.5 mr-2" /> Add Season 1
                </Button>
              </div>
            )}

            {seriesData.seasons.length > 0 && (
              <>
                <div className="flex gap-1 mb-6">
                  {seriesData.seasons.map((s, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveSeason(idx)}
                      className={`px-4 py-2 font-mono text-xs uppercase tracking-wider border transition-all ${
                        activeSeason === idx
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-card border-border/40 text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      Season {s.season_number}
                    </button>
                  ))}
                </div>

                {currentSeason && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between border-b border-border/40 pb-3">
                      <span className="font-mono text-xs uppercase tracking-wider text-primary">Season {currentSeason.season_number}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteSeason(activeSeason)}
                        className="rounded-none font-mono uppercase tracking-widest text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                        data-testid={`delete-season-${activeSeason}`}
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete Season
                      </Button>
                    </div>
                    {[
                      { field: 'logline',          label: 'Season Logline',       placeholder: 'What is this season about in one or two sentences? What is the season-long dramatic question?' },
                      { field: 'theme',             label: 'Season Theme',         placeholder: 'What is this season exploring thematically? What question does it ask about your characters or your world?' },
                      { field: 'character_arcs',    label: 'Character Arcs',       placeholder: 'How does each major character change (or refuse to change) across this season? What do they learn, lose, or become?' },
                      { field: 'mid_season_turn',   label: 'Mid-Season Turn',      placeholder: 'The moment halfway through the season when everything shifts. What event or revelation changes the shape of the back half?' },
                      { field: 'finale_direction',  label: 'Finale Direction',     placeholder: "Where does the season end? What is resolved, what is left open? How does the finale set up the next season?" },
                    ].map(({ field, label, placeholder }) => (
                      <div key={field}>
                        <label className="block font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2">{label}</label>
                        <AutoExpandTextarea
                          placeholder={placeholder}
                          value={currentSeason[field] || ''}
                          onChange={e => updateSeason(activeSeason, field, e.target.value)}
                          className="bg-background border-input rounded-none font-mono text-sm focus:ring-2 focus:ring-primary placeholder:text-muted-foreground/50 min-h-[80px]"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        );
      case 'episodes':
        return (
          <div>
            <div className="flex items-start justify-between gap-3 flex-wrap mb-6">
              <div>
                <h2 className="font-mono text-xl font-semibold mb-1">Episodes</h2>
                <p className="text-muted-foreground text-sm font-mono">
                  Episode 1 uses pilot beat structure (12 beats). Episodes 2+ use the standard episode structure (10 beats).
                </p>
              </div>
              {splitProps && <SplitViewTrigger {...splitProps} />}
            </div>

            {seriesData.seasons.length === 0 && (
              <div className="border border-border/40 p-12 text-center">
                <p className="font-mono text-sm text-muted-foreground mb-4">Add a season first before planning episodes.</p>
                <Button onClick={() => setActiveTab('seasons')} variant="outline" className="rounded-none font-mono uppercase tracking-widest text-xs">
                  Go to Season Arc
                </Button>
              </div>
            )}

            {seriesData.seasons.length > 0 && (
              <>
                {/* Season selector */}
                <div className="flex gap-1 mb-6">
                  {seriesData.seasons.map((s, idx) => (
                    <button
                      key={idx}
                      onClick={() => { setActiveSeason(idx); setActiveEpisode(null); }}
                      className={`px-4 py-2 font-mono text-xs uppercase tracking-wider border transition-all ${
                        activeSeason === idx
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-card border-border/40 text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      Season {s.season_number}
                    </button>
                  ))}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Episode list */}
                  <div className="md:col-span-1">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                        {currentSeason?.episodes?.length || 0} Episode{(currentSeason?.episodes?.length || 0) !== 1 ? 's' : ''}
                      </span>
                      <button
                        onClick={() => addEpisode(activeSeason)}
                        className="flex items-center gap-1 font-mono text-xs text-primary hover:text-primary/80 uppercase tracking-wider transition-colors"
                      >
                        <Plus className="w-3 h-3" /> Add
                      </button>
                    </div>

                    <div className="space-y-1">
                      {(currentSeason?.episodes || []).map((ep, epIdx) => {
                        const isActive = activeEpisode?.seasonIdx === activeSeason && activeEpisode?.episodeIdx === epIdx;
                        const epBeats  = getBeatsForEpisode(ep.episode_number);
                        const filled   = epBeats.filter(b => ep.beats?.[b.key]?.trim()).length;
                        return (
                          <div
                            key={epIdx}
                            onClick={() => setActiveEpisode({ seasonIdx: activeSeason, episodeIdx: epIdx })}
                            className={`group w-full text-left px-4 py-3 border transition-all cursor-pointer ${
                              isActive
                                ? 'bg-primary/10 border-primary/40 text-foreground'
                                : 'bg-card border-border/40 text-muted-foreground hover:text-foreground hover:border-border'
                            }`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-mono text-xs uppercase tracking-wider">
                                {ep.episode_number === 1 ? 'Pilot' : `Ep ${ep.episode_number}`}
                              </span>
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-xs text-muted-foreground">{filled}/{epBeats.length}</span>
                                <button
                                  onClick={(e) => { e.stopPropagation(); deleteEpisode(activeSeason, epIdx); }}
                                  className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                                  aria-label="Delete episode"
                                  data-testid={`delete-episode-${epIdx}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                            {ep.title && <p className="font-mono text-xs mt-1 truncate text-foreground">{ep.title}</p>}
                          </div>
                        );
                      })}


                      {(currentSeason?.episodes?.length || 0) === 0 && (
                        <div className="px-4 py-6 border border-border/20 text-center">
                          <p className="font-mono text-xs text-muted-foreground">No episodes yet.</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Beat sheet */}
                  <div className="md:col-span-2">
                    {!currentEpisode && (
                      <div className="border border-border/40 p-10 flex items-center justify-center" style={{ minHeight: '320px' }}>
                        <p className="font-mono text-sm text-muted-foreground">Select an episode to edit its beat sheet.</p>
                      </div>
                    )}

                    {currentEpisode && (
                      <div>
                        {/* Episode meta */}
                        <div className="space-y-3 mb-5">
                          <div className="font-mono text-xs uppercase tracking-wider text-primary mb-1">
                            {currentEpisode.episode_number === 1
                              ? 'Pilot — 12-Beat Structure'
                              : `Episode ${currentEpisode.episode_number} — 10-Beat Structure`}
                          </div>
                          <AutoExpandTextarea
                            placeholder="Episode title"
                            value={currentEpisode.title || ''}
                            onChange={e => updateEpisodeMeta(activeEpisode.seasonIdx, activeEpisode.episodeIdx, 'title', e.target.value)}
                            className="bg-background border-input rounded-none font-mono text-sm min-h-[50px]"
                          />
                          <AutoExpandTextarea
                            placeholder="Episode logline — what is this episode about in one sentence?"
                            value={currentEpisode.logline || ''}
                            onChange={e => updateEpisodeMeta(activeEpisode.seasonIdx, activeEpisode.episodeIdx, 'logline', e.target.value)}
                            className="bg-background border-input rounded-none font-mono text-sm placeholder:text-muted-foreground/50 min-h-[60px]"
                          />
                        </div>

                        {/* Beats grouped by act */}
                        {[1, 2, 3].map(act => {
                          const actBeats = currentEpBeats.filter(b => b.act === act);
                          const filledInAct = actBeats.filter(b => currentEpisode.beats?.[b.key]?.trim()).length;
                          return (
                            <div key={act} className="mb-2 border border-border/40">
                              <button
                                onClick={() => toggleAct(act)}
                                className="w-full flex items-center justify-between px-4 py-3 bg-card hover:bg-card/70 transition-colors"
                              >
                                <span className="font-mono text-xs uppercase tracking-wider text-primary">{ACT_LABELS[act]}</span>
                                <div className="flex items-center gap-3">
                                  <span className="font-mono text-xs text-muted-foreground">{filledInAct}/{actBeats.length}</span>
                                  {expandedActs[act]
                                    ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" />
                                    : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />}
                                </div>
                              </button>

                              {expandedActs[act] && (
                                <div className="p-4 space-y-4">
                                  {actBeats.map(beat => (
                                    <div key={beat.key}>
                                      <label className="block font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1.5">
                                        {beat.label}
                                      </label>
                                      <AutoExpandTextarea
                                        placeholder={beat.placeholder}
                                        value={currentEpisode.beats?.[beat.key] || ''}
                                        onChange={e => updateEpisodeBeat(
                                          activeEpisode.seasonIdx,
                                          activeEpisode.episodeIdx,
                                          beat.key,
                                          e.target.value
                                        )}
                                        className="bg-background border-input rounded-none font-mono text-sm placeholder:text-muted-foreground/50 min-h-[80px]"
                                      />
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        );
      // ── Borrowed workspace modules ─────────────────────────────────────
      case 'screenplay':
        // One screenplay per project (the series' pilot/spec script) — the
        // same data-model rule as the workspace. Per-episode scripts would
        // be a data-model change, tracked separately.
        return (
          <ScreenplayTab
            splitProps={splitProps}
            docRef={screenplayDocRef}
            project={project}
          />
        );
      case 'notes':
        return <NotesTab project={project} navigate={navigate} splitProps={splitProps} />;
      case 'characters':
        return (
          <CharactersTab
            characters={characters}
            plotProject={project}
            navigate={navigate}
            onRefresh={fetchData}
            splitProps={splitProps}
          />
        );
      case 'web':
        return (
          <CharacterWebTab
            characters={characters}
            relationships={relationships}
            setRelationships={setRelationships}
            plotProject={project}
            user={user}
            navigate={navigate}
            splitProps={splitProps}
          />
        );
      case 'world':
        return <WorldBuilding embeddedProjectId={projectId} splitProps={splitProps} />;
      default:
        return (
          <div className="text-muted-foreground font-mono text-sm py-12 text-center">
            {SERIES_TAB_LABELS[tabId] || tabId} isn't available inside Series Builder.
          </div>
        );
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`${API}/series/${projectId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(seriesData),
      });
      if (res.status === 403) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Series Builder requires the Studio plan.');
        navigate('/upgrade');
        return;
      }
      if (!res.ok) throw new Error();
      const updated = await res.json();
      setProject(updated);
      toast.success('Saved');
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // ─── State helpers ──────────────────────────────────────────────────────

  const updateBible = (field, value) =>
    setSeriesData(prev => ({ ...prev, series_bible: { ...prev.series_bible, [field]: value } }));

  const updateSeason = (seasonIdx, field, value) =>
    setSeriesData(prev => {
      const seasons = [...prev.seasons];
      seasons[seasonIdx] = { ...seasons[seasonIdx], [field]: value };
      return { ...prev, seasons };
    });

  const updateEpisodeMeta = (seasonIdx, episodeIdx, field, value) =>
    setSeriesData(prev => {
      const seasons = [...prev.seasons];
      const episodes = [...(seasons[seasonIdx].episodes || [])];
      episodes[episodeIdx] = { ...episodes[episodeIdx], [field]: value };
      seasons[seasonIdx] = { ...seasons[seasonIdx], episodes };
      return { ...prev, seasons };
    });

  const updateEpisodeBeat = (seasonIdx, episodeIdx, beatKey, value) =>
    setSeriesData(prev => {
      const seasons = [...prev.seasons];
      const episodes = [...(seasons[seasonIdx].episodes || [])];
      episodes[episodeIdx] = {
        ...episodes[episodeIdx],
        beats: { ...(episodes[episodeIdx].beats || {}), [beatKey]: value },
      };
      seasons[seasonIdx] = { ...seasons[seasonIdx], episodes };
      return { ...prev, seasons };
    });

  const addSeason = () => {
    if (!isIndiePlus && seriesData.seasons.length >= 1) {
      toast.error('Spark includes 1 season. Upgrade to Indie for unlimited seasons.');
      return;
    }
    if (seriesData.seasons.length >= 3) {
      toast.error('Maximum 3 seasons in the Series Builder.');
      return;
    }
    const newIdx = seriesData.seasons.length;
    setSeriesData(prev => ({
      ...prev,
      seasons: [...prev.seasons, EMPTY_SEASON(prev.seasons.length + 1)],
    }));
    setActiveSeason(newIdx);
    setActiveEpisode(null);
  };

  const addEpisode = (seasonIdx) => {
    const epCount = seriesData.seasons[seasonIdx]?.episodes?.length || 0;
    if (!isIndiePlus && epCount >= 3) {
      toast.error('Spark includes 3 episodes per season. Upgrade to Indie for unlimited episodes.');
      return;
    }
    const newEpIdx = epCount;
    setSeriesData(prev => {
      const seasons = [...prev.seasons];
      const episodes = [...(seasons[seasonIdx].episodes || [])];
      episodes.push(EMPTY_EPISODE(episodes.length + 1));
      seasons[seasonIdx] = { ...seasons[seasonIdx], episodes };
      return { ...prev, seasons };
    });
    setActiveEpisode({ seasonIdx, episodeIdx: newEpIdx });
  };

  // Persist an explicit series object immediately (used by destructive actions
  // like delete, which should stick without waiting for the manual Save button —
  // matching how the other modules persist edits/deletes per action).
  const persistSeries = async (nextData) => {
    setSeriesData(nextData);
    try {
      const res = await authFetch(`${API}/series/${projectId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(nextData),
      });
      if (res.status === 403) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Series Builder requires the Studio plan.');
        navigate('/upgrade');
        return false;
      }
      if (!res.ok) throw new Error();
      setProject(await res.json());
      return true;
    } catch {
      toast.error('Failed to save changes');
      return false;
    }
  };

  const deleteSeason = async (seasonIdx) => {
    const s = seriesData.seasons[seasonIdx];
    if (!window.confirm(`Delete Season ${s?.season_number ?? seasonIdx + 1} and all its episodes? This cannot be undone.`)) return;
    // Remove + renumber remaining seasons so numbers stay gapless.
    const seasons = seriesData.seasons
      .filter((_, i) => i !== seasonIdx)
      .map((season, i) => ({ ...season, season_number: i + 1 }));
    const nextData = { ...seriesData, seasons };
    // Keep the active season in range and drop any episode selection in the removed season.
    setActiveSeason(prev => Math.max(0, Math.min(prev, seasons.length - 1)));
    setActiveEpisode(null);
    if (await persistSeries(nextData)) toast.success('Season deleted');
  };

  const deleteEpisode = async (seasonIdx, episodeIdx) => {
    const ep = seriesData.seasons[seasonIdx]?.episodes?.[episodeIdx];
    const epLabel = ep?.episode_number === 1 ? 'the Pilot' : `Episode ${ep?.episode_number ?? episodeIdx + 1}`;
    if (!window.confirm(`Delete ${epLabel}? This cannot be undone.`)) return;
    const seasons = [...seriesData.seasons];
    const episodes = (seasons[seasonIdx].episodes || [])
      .filter((_, i) => i !== episodeIdx)
      .map((e, i) => ({ ...e, episode_number: i + 1 }));
    seasons[seasonIdx] = { ...seasons[seasonIdx], episodes };
    const nextData = { ...seriesData, seasons };
    setActiveEpisode(null);
    if (await persistSeries(nextData)) toast.success('Episode deleted');
  };

  const toggleAct = (act) =>
    setExpandedActs(prev => ({ ...prev, [act]: !prev[act] }));

  const handleWorldTab = () => {
    setActiveTab('world');
  };

  // ─── Loading ─────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-foreground font-mono">Loading series...</div>
      </div>
    );
  }

  const progress        = project?.progress || 0;
  const currentSeason   = seriesData.seasons[activeSeason];
  const currentEpisode  = activeEpisode
    ? seriesData.seasons[activeEpisode.seasonIdx]?.episodes?.[activeEpisode.episodeIdx]
    : null;
  const currentEpBeats  = currentEpisode ? getBeatsForEpisode(currentEpisode.episode_number) : [];

  // ─── Render ──────────────────────────────────────────────────────────────

  // Series Builder is Studio-only on the server (create + every save 403 below
  // Studio). Show the upgrade path instead of a builder that cannot save.
  if (user && !isStudioTier(user.subscription_tier)) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <div className="noise-overlay" />
        <div className="bg-card border border-border/40 p-16 text-center max-w-lg mx-auto mt-24" data-testid="series-builder-upgrade">
          <Lock className="w-16 h-16 text-muted-foreground mx-auto mb-6" />
          <h3 className="font-mono text-xl font-semibold mb-3">Series Builder is a Studio feature</h3>
          <p className="text-muted-foreground mb-6">
            Series bible, season arcs and episode beat sheets — pilot to finale — are part of the
            Studio plan (KES 1,299/mo), or Founding for life. {project?.title ? `"${project.title}" is waiting.` : ''}
          </p>
          <div className="flex gap-3 justify-center">
            <Button
              onClick={() => navigate('/upgrade')}
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none font-mono uppercase tracking-widest text-xs px-6 py-3"
            >
              Upgrade to Studio
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard')}
              className="rounded-none font-mono uppercase tracking-widest text-xs px-6 py-3"
            >
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="noise-overlay" />

      {/* Header */}
      <header className="glassmorphism sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-2">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            aria-label="Back to Dashboard"
            className="rounded-none font-mono uppercase tracking-widest text-xs flex-shrink-0 px-2 sm:px-4"
          >
            <ArrowLeft className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Dashboard</span>
          </Button>
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <Tv className="w-4 h-4 text-primary flex-shrink-0" />
              <span className="text-sm font-mono truncate">{project?.title}</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setExportOpen(true)}
              aria-label="Export Bible"
              className="rounded-none font-mono uppercase tracking-widest text-xs flex-shrink-0 px-2 sm:px-3"
            >
              <FileDown className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Export Bible</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setHistoryOpen(true)}
              aria-label="Version History"
              className="rounded-none font-mono uppercase tracking-widest text-xs flex-shrink-0 px-2 sm:px-3"
            >
              <History className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">History</span>
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none font-mono uppercase tracking-widest text-xs px-3 sm:px-4 flex-shrink-0"
            >
              <Save className="w-3.5 h-3.5 mr-2" />
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-mono text-muted-foreground uppercase tracking-wider">Series Progress</span>
            <span className="text-xs font-mono text-primary">{progress}%</span>
          </div>
          <div className="relative h-1.5 bg-secondary">
            <div className="absolute inset-y-0 left-0 bg-primary transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>


        {/* Tabs */}
        <div className="flex flex-wrap border-b border-border/40 mb-8">
          {[
            { id: 'bible',      label: 'Series Bible' },
            { id: 'seasons',    label: 'Season Arc' },
            { id: 'episodes',   label: 'Episodes' },
            { id: 'screenplay', label: 'Script' },
            { id: 'characters', label: 'Characters' },
            { id: 'web',        label: 'Character Web' },
            { id: 'world',      label: 'World' },
            { id: 'notes',      label: 'Notes' },
            { id: 'mentor',     label: 'AI Mentor' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => tab.id === 'world' ? handleWorldTab() : setActiveTab(tab.id)}
              className={`px-5 py-3 font-mono text-xs uppercase tracking-widest border-b-2 transition-all -mb-px ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Split-capable modules: native (Series Bible · Season Arc ·
            Episodes) + borrowed (Characters · Web · World · Notes) ──
            Each renders its own SplitViewTrigger via splitProps; when a
            partner module is picked they sit side-by-side in
            SplitViewLayout (mirrors the workspace behaviour). */}
        {SERIES_SPLIT_TABS.includes(activeTab) && (
          splitView?.right
          && splitView.right !== activeTab
          && SERIES_SPLIT_TABS.includes(splitView.right) ? (
            <SplitViewLayout
              leftLabel={SERIES_TAB_LABELS[activeTab] || activeTab}
              rightLabel={SERIES_TAB_LABELS[splitView.right] || splitView.right}
              onClose={() => updateSplitView(null)}
              left={renderSeriesModule(activeTab, {
                primaryTab: activeTab, splitView,
                onChangeSplit: updateSplitView, availableTabs: SERIES_SPLIT_TABS, labels: SERIES_TAB_LABELS, pane: 'left',
              })}
              right={renderSeriesModule(splitView.right, {
                primaryTab: splitView.right, splitView,
                onChangeSplit: updateSplitView, availableTabs: SERIES_SPLIT_TABS, labels: SERIES_TAB_LABELS, pane: 'right',
              })}
            />
          ) : (
            renderSeriesModule(activeTab, {
              primaryTab: activeTab, splitView,
              onChangeSplit: updateSplitView, availableTabs: SERIES_SPLIT_TABS, labels: SERIES_TAB_LABELS,
            })
          )
        )}

        {/* ── AI Mentor ─────────────────────────────────────────── */}
        {activeTab === 'mentor' && (
          <SeriesMentorPanel project={project} user={user} language={i18n.language} />
        )}
      </main>

      {exportOpen && (
        <SeriesExportModal
          project={project}
          seriesData={seriesData}
          characters={characters}
          relationships={relationships}
          user={user}
          onClose={() => setExportOpen(false)}
        />
      )}

      {historyOpen && (
        <VersionHistoryModal
          projectId={projectId}
          onClose={() => setHistoryOpen(false)}
          onRestored={() => {
            // Kill any pending screenplay autosave/flush FIRST — the
            // reload's beforeunload would otherwise PUT the pre-restore
            // draft over the document the restore just wrote (same fix as
            // ProjectWorkspace; needed here since the Script tab landed).
            window.dispatchEvent(new Event('pangaji:screenplay-suppress-flush'));
            window.location.reload();
          }}
        />
      )}
    </div>
  );
}

// ─── Series Export Modal ──────────────────────────────────────────────────

const SERIES_EXPORT_SECTIONS = [
  { key: 'series_bible', label: 'Series Bible',    description: 'Logline, premise, tone, platform, episode and season counts' },
  { key: 'seasons',      label: 'Season Arcs',     description: 'All season arcs — logline, theme, character arcs, mid-season turn, finale' },
  { key: 'episodes',     label: 'Episodes',        description: 'Episode list with loglines and beat sheets' },
  { key: 'characters',   label: 'Characters',      description: 'Full character sheets and biography' },
  { key: 'relationships',label: 'Relationships',   description: 'Character web and conflict data' },
  { key: 'world',        label: 'World Building',  description: 'Locations, society, economy, history' },
];

function buildSeriesPreview(sections, project, seriesData, characters, relationships) {
  const lines = [];
  const bible = seriesData?.series_bible || {};
  const seasons = seriesData?.seasons || [];

  if (sections.includes('series_bible')) {
    lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    lines.push(`SERIES BIBLE — ${project?.title}`);
    lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    if (bible.logline)       lines.push(`Logline: ${bible.logline}`);
    if (bible.premise)       lines.push(`\nPremise:\n${bible.premise}`);
    if (bible.tone)          lines.push(`\nTone: ${bible.tone}`);
    if (bible.platform)      lines.push(`Platform: ${bible.platform}`);
    if (bible.episode_count) lines.push(`Episodes per season: ${bible.episode_count}`);
    if (bible.season_count)  lines.push(`Planned seasons: ${bible.season_count}`);
    lines.push('');
  }

  if (sections.includes('seasons') && seasons.length) {
    lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    lines.push('SEASON ARCS');
    lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    seasons.forEach(s => {
      lines.push(`\nSeason ${s.season_number}:`);
      if (s.logline)          lines.push(`  Logline: ${s.logline}`);
      if (s.theme)            lines.push(`  Theme: ${s.theme}`);
      if (s.character_arcs)   lines.push(`  Character Arcs: ${s.character_arcs}`);
      if (s.mid_season_turn)  lines.push(`  Mid-Season Turn: ${s.mid_season_turn}`);
      if (s.finale_direction) lines.push(`  Finale Direction: ${s.finale_direction}`);
    });
    lines.push('');
  }

  if (sections.includes('episodes') && seasons.length) {
    lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    lines.push('EPISODES');
    lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    seasons.forEach(s => {
      const eps = s.episodes || [];
      if (!eps.length) return;
      lines.push(`\nSeason ${s.season_number}:`);
      eps.forEach(ep => {
        const label = ep.episode_number === 1 ? 'Pilot' : `Ep ${ep.episode_number}`;
        lines.push(`  ${label}${ep.title ? ': ' + ep.title : ''}${ep.logline ? ' — ' + ep.logline : ''}`);
      });
    });
    lines.push('');
  }

  if (sections.includes('characters') && characters.length) {
    lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    lines.push('CHARACTERS');
    lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    characters.forEach(c => {
      const name = c.level_0?.name || c.title;
      lines.push(`\n${name} — ${(c.character_role || '').replace('_', ' ')} (${c.arc_type} arc)`);
      if (c.level_0?.biography) lines.push(`Bio: ${c.level_0.biography}`);
    });
    lines.push('');
  }

  if (sections.includes('relationships') && relationships.length) {
    lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    lines.push('RELATIONSHIPS');
    lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    const nameMap = Object.fromEntries(characters.map(c => [c.project_id, c.level_0?.name || c.title]));
    relationships.forEach(r => {
      lines.push(`\n${nameMap[r.protagonist_id] || 'A'} ↔ ${nameMap[r.target_id] || 'B'}`);
      if (r.central_argument) lines.push(`  Central argument: ${r.central_argument}`);
    });
    lines.push('');
  }

  if (sections.includes('world')) {
    lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    lines.push('WORLD BUILDING');
    lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    lines.push('(Locations, society, economy, history and more will be included from your World Building data.)');
    lines.push('');
  }

  return lines.join('\n') || 'Select sections above to preview content.';
}

function SeriesExportModal({ project, seriesData, characters, relationships, user, onClose }) {
  const isIndiePlus = ['indie', 'studio'].includes(user?.subscription_tier);
  const today = new Date().toISOString().split('T')[0];
  const safeTitle = (project?.title || 'Series').replace(/[^a-zA-Z0-9]/g, '_');

  const [selectedSections, setSelectedSections] = useState(
    SERIES_EXPORT_SECTIONS.map(s => s.key)
  );
  const [filename, setFilename] = useState(`${safeTitle}_Bible_${today}`);
  const [downloading, setDownloading] = useState(false);

  const toggleSection = (key) =>
    setSelectedSections(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);

  const handleDownload = async () => {
    if (!selectedSections.length) { toast.error('Select at least one section'); return; }
    setDownloading(true);
    try {
      const response = await authFetch(`${API}/export/story-bible`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ project_id: project.project_id, sections: selectedSections }),
      });
      if (!response.ok) {
        // Mirror the workspace Export Center: only 4xx detail strings are
        // shown to users; 5xx bodies and network errors stay generic.
        let msg = 'Export failed — try again';
        if (response.status >= 400 && response.status < 500) {
          const detail = await response.json().then(d => d?.detail).catch(() => null);
          if (typeof detail === 'string' && detail) msg = detail.slice(0, 200);
        }
        const err = new Error(msg);
        err.userFacing = true;
        throw err;
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filename}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Series Bible exported!');
      onClose();
    } catch (err) {
      toast.error(err?.userFacing ? err.message : 'Export failed — try again');
    } finally {
      setDownloading(false);
    }
  };

  const preview = buildSeriesPreview(selectedSections, project, seriesData, characters, relationships);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={onClose}>
      <div
        className="bg-card border border-border/40 w-full max-w-4xl max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border/40 flex-shrink-0">
          <div className="flex items-center gap-3">
            <FileDown className="w-5 h-5 text-primary" />
            <h2 className="font-mono text-lg font-semibold tracking-tight">Export Center</h2>
            {!isIndiePlus && (
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground border border-border/40 px-2 py-0.5">
                Spark — watermark included
              </span>
            )}
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex flex-1 min-h-0">
          {/* Left panel */}
          <div className="w-72 flex-shrink-0 border-r border-border/40 flex flex-col p-6 gap-6 overflow-y-auto">
            <div>
              <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-3">Sections</p>
              <div className="space-y-2">
                {SERIES_EXPORT_SECTIONS.map(sec => (
                  <label key={sec.key} className="flex items-start gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={selectedSections.includes(sec.key)}
                      onChange={() => toggleSection(sec.key)}
                      className="mt-0.5 accent-primary"
                    />
                    <div>
                      <p className="font-mono text-sm font-medium group-hover:text-primary transition-colors">{sec.label}</p>
                      <p className="text-xs text-muted-foreground">{sec.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="font-mono text-xs uppercase tracking-widest text-muted-foreground block mb-2">Filename</label>
              <div className="flex items-center">
                <input
                  type="text"
                  value={filename}
                  onChange={e => setFilename(e.target.value)}
                  className="flex-1 bg-background border border-input px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <span className="font-mono text-xs text-muted-foreground ml-1">.pdf</span>
              </div>
            </div>

            <Button
              onClick={handleDownload}
              disabled={downloading || !selectedSections.length}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-none font-mono uppercase tracking-widest text-xs py-3 shadow-glow mt-auto"
            >
              {downloading
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Downloading...</>
                : <><FileDown className="w-4 h-4 mr-2" /> Download PDF</>}
            </Button>
          </div>

          {/* Right panel — preview */}
          <div className="flex-1 flex flex-col min-w-0">
            <div className="px-6 py-3 border-b border-border/40 flex-shrink-0">
              <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Preview</p>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {selectedSections.length === 0
                ? <p className="text-muted-foreground font-mono text-sm">No sections selected.</p>
                : <pre className="font-mono text-xs text-foreground leading-relaxed whitespace-pre-wrap break-words">{preview}</pre>
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Series Mentor Panel ──────────────────────────────────────────────────

function SeriesMentorPanel({ project, user, language }) {
  const navigate = useNavigate();
  const isIndiePlus = ['indie', 'studio'].includes(user?.subscription_tier);

  const [isOpen,         setIsOpen]         = useState(false);
  const [mode,           setMode]           = useState('forge_guide');
  const [history,        setHistory]        = useState([]);
  const [input,          setInput]          = useState('');
  const [loading,        setLoading]        = useState(false);
  const [usesRemaining,  setUsesRemaining]  = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history]);

  const callMentor = async (currentMode, currentHistory, message) => {
    setLoading(true);
    try {
      const body = {
        project_id: project.project_id,
        mode: currentMode,
        history: currentHistory,
        language: language || 'en',
      };
      if (message) body.message = message;

      const res = await authFetch(`${API}/ai/plot-mentor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (res.status === 403) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Upgrade to Indie for unlimited AI Mentor access');
        setIsOpen(false);
        return null;
      }
      if (!res.ok) { toast.error('AI Mentor unavailable — try again'); return null; }

      const data = await res.json();
      if (data.uses_remaining !== undefined && data.uses_remaining !== null) {
        setUsesRemaining(data.uses_remaining);
      }
      return data.reply;
    } catch {
      toast.error('AI Mentor unavailable — check your connection');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = async () => {
    setIsOpen(true);
    setHistory([]);
    setInput('');
    const reply = await callMentor(mode, [], null);
    if (reply) setHistory([{ role: 'assistant', content: reply }]);
  };

  const handleModeSwitch = async (newMode) => {
    setMode(newMode);
    setHistory([]);
    setInput('');
    const reply = await callMentor(newMode, [], null);
    if (reply) setHistory([{ role: 'assistant', content: reply }]);
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    const userMessage = input.trim();
    setInput('');
    const newHistory = [...history, { role: 'user', content: userMessage }];
    setHistory(newHistory);
    const reply = await callMentor(mode, history, userMessage);
    if (reply) setHistory([...newHistory, { role: 'assistant', content: reply }]);
  };

  const currentMode = SERIES_MENTOR_MODES.find(m => m.key === mode);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Brain className="w-5 h-5 text-primary" />
          <div>
            <h3 className="font-mono text-lg font-semibold tracking-tight">Series AI Mentor</h3>
            <p className="text-xs text-muted-foreground font-mono">
              Reads your full series — bible, season arcs, and episodes — then coaches you.
            </p>
          </div>
        </div>
        {!isIndiePlus && usesRemaining !== null && (
          <span className="font-mono text-xs text-muted-foreground border border-border/40 px-3 py-1.5">
            {usesRemaining} uses left this month
          </span>
        )}
      </div>

      {!isOpen ? (
        <div className="bg-card border border-border/40 p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            {SERIES_MENTOR_MODES.map(m => (
              <button
                key={m.key}
                onClick={() => setMode(m.key)}
                className={`p-4 text-left border transition-all ${
                  mode === m.key
                    ? 'border-primary/60 bg-primary/10'
                    : 'border-border/40 hover:border-primary/40'
                }`}
              >
                <div className="font-mono text-sm font-semibold mb-1">{m.icon} {m.label}</div>
                <div className="text-xs text-muted-foreground">{m.desc}</div>
              </button>
            ))}
          </div>
          <Button
            onClick={handleOpen}
            disabled={!project}
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none font-mono uppercase tracking-widest text-xs px-6 py-3 shadow-glow"
          >
            <Brain className="w-4 h-4 mr-2" />
            {mode === 'forge_guide' ? 'Start Series Coaching' : 'Run Series Stress Test'}
          </Button>
        </div>
      ) : (
        <div className="bg-card border border-border/40">
          {/* Panel header */}
          <div className="flex items-center justify-between p-4 border-b border-border/40">
            <div className="flex items-center gap-3">
              <Brain className="w-4 h-4 text-primary" />
              <span className="font-mono text-sm font-semibold">
                {currentMode?.icon} {currentMode?.label}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {SERIES_MENTOR_MODES.map(m => (
                <button
                  key={m.key}
                  onClick={() => handleModeSwitch(m.key)}
                  disabled={loading}
                  className={`px-2.5 py-1 font-mono text-xs transition-all disabled:opacity-50 ${
                    mode === m.key
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-background border border-border/40 text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {m.icon} {m.label}
                </button>
              ))}
              <button
                onClick={() => setIsOpen(false)}
                className="text-muted-foreground hover:text-foreground font-mono text-xs ml-2 px-2 py-1 border border-border/40"
              >
                Close
              </button>
            </div>
          </div>

          {/* Chat area */}
          <div className="h-80 overflow-y-auto p-4 space-y-4">
            {history.length === 0 && loading && (
              <div className="flex items-center gap-2 text-muted-foreground text-sm font-mono">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Reading your series...</span>
              </div>
            )}
            {history.map((msg, i) => (
              <div
                key={i}
                className={
                  msg.role === 'assistant'
                    ? 'bg-primary/10 border border-primary/30 p-4 text-sm font-mono leading-relaxed'
                    : 'bg-secondary/40 p-3 text-sm text-right ml-8 font-mono leading-relaxed'
                }
              >
                {msg.role === 'assistant' && (
                  <div className="text-xs text-primary uppercase tracking-wider mb-2 font-mono">
                    {currentMode?.icon} {currentMode?.label}
                  </div>
                )}
                {msg.content}
              </div>
            ))}
            {loading && history.length > 0 && (
              <div className="flex items-center gap-2 text-muted-foreground text-xs font-mono">
                <Loader2 className="w-3 h-3 animate-spin" />
                <span>Thinking...</span>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-border/40 flex gap-2">
            <input
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSend()}
              placeholder="Reply to your mentor..."
              disabled={loading}
              className="flex-1 bg-background border border-input px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
            />
            <Button
              onClick={handleSend}
              disabled={loading || !input.trim()}
              className="bg-primary text-primary-foreground rounded-none px-3"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
